import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, X, MapPin, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const contractTypes = [
  { value: 'CDI', label: 'CDI' },
  { value: 'CDD', label: 'CDD' },
  { value: 'Stage', label: 'Stage' },
  { value: 'Alternance', label: 'Alternance' },
  { value: 'Intérim', label: 'Intérim' },
];

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    contract_type: 'CDI',
    description: '',
    requirements: []
  });
  const [requirementsText, setRequirementsText] = useState('');

  const fetchJobs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/jobs`, { withCredentials: true });
      setJobs(data);
    } catch (e) {
      toast.error('Erreur lors du chargement des offres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleEdit = (job) => {
    setSelectedJob(job);
    setFormData({
      title: job.title,
      location: job.location,
      contract_type: job.contract_type,
      description: job.description,
      requirements: job.requirements || []
    });
    setRequirementsText((job.requirements || []).join('\n'));
    setEditDialog(true);
  };

  const handleCreate = () => {
    setSelectedJob(null);
    setFormData({
      title: '',
      location: '',
      contract_type: 'CDI',
      description: '',
      requirements: []
    });
    setRequirementsText('');
    setEditDialog(true);
  };

  const handleDelete = (job) => {
    setSelectedJob(job);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/admin/jobs/${selectedJob.id}`, { withCredentials: true });
      toast.success('Offre supprimée');
      fetchJobs();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialog(false);
      setSelectedJob(null);
    }
  };

  const handleSave = async () => {
    const requirements = requirementsText.split('\n').filter(r => r.trim());
    const payload = { ...formData, requirements };

    try {
      if (selectedJob) {
        await axios.put(`${API}/api/admin/jobs/${selectedJob.id}`, payload, { withCredentials: true });
        toast.success('Offre mise à jour');
      } else {
        await axios.post(`${API}/api/admin/jobs`, payload, { withCredentials: true });
        toast.success('Offre créée');
      }
      fetchJobs();
      setEditDialog(false);
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleJobActive = async (job) => {
    try {
      await axios.put(`${API}/api/admin/jobs/${job.id}`, { is_active: !job.is_active }, { withCredentials: true });
      toast.success(job.is_active ? 'Offre désactivée' : 'Offre activée');
      fetchJobs();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white h-20 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-jobs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-1">Offres d'emploi</h1>
          <p className="text-slate-600">Gérez les offres de recrutement</p>
        </div>
        <Button onClick={handleCreate} className="bg-navy hover:bg-navy/90 text-white rounded-sm" data-testid="create-job-btn">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une offre
        </Button>
      </div>

      {/* Jobs List */}
      <div className="bg-white border border-slate-200">
        <div className="divide-y divide-slate-100">
          {jobs.map((job) => (
            <div key={job.id} className={`p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 ${!job.is_active ? 'opacity-50' : ''}`} data-testid={`job-row-${job.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading font-bold text-lg text-slate-900">{job.title}</h3>
                  {!job.is_active && (
                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-4 w-4" />
                    {job.contract_type}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => toggleJobActive(job)}
                  className={job.is_active ? 'text-emerald-600' : 'text-slate-400'}
                >
                  {job.is_active ? 'Active' : 'Inactive'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(job)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(job)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {jobs.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              Aucune offre d'emploi. Cliquez sur "Ajouter une offre" pour commencer.
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedJob ? 'Modifier l\'offre' : 'Nouvelle offre'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre du poste *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Technicien de production"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Localisation *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Esternay (51)"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type de contrat *</Label>
                <Select value={formData.contract_type} onValueChange={(val) => setFormData({ ...formData, contract_type: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {contractTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description du poste *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="mt-1"
                placeholder="Décrivez les missions et responsabilités..."
              />
            </div>

            <div>
              <Label>Profil recherché (un critère par ligne)</Label>
              <Textarea
                value={requirementsText}
                onChange={(e) => setRequirementsText(e.target.value)}
                rows={4}
                placeholder="Bac+2 minimum&#10;3 ans d'expérience&#10;Maîtrise des outils..."
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                <X className="h-4 w-4 mr-2" />
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-navy hover:bg-navy/90 text-white">
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette offre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'offre "{selectedJob?.title}" sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminJobs;
